// ═══════════════════════════════════════════════════════════
// V·IANet Requests — Mis Solicitudes + Pendientes de Aprobar
// ═══════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { request } from '../../shared/services/api';
import { useI18n } from '../../../i18n';
import { InfoTooltip } from '@/components/ui/InfoTooltip';

interface SolicitudResumen {
  id: string;
  codigo: string;
  tipoSolicitud?: { codigo: string; nombre: string };
  estado: string;
  prioridad?: string;
  creadoEn: string;
  actualizadoEn: string;
  solicitante?: { nombre: string; apellidos: string };
}

const ESTADO_COLORS: Record<string, { bg: string; text: string }> = {
  BORRADOR: { bg: '#F3F4F6', text: '#6B7280' },
  ENVIADA: { bg: '#DBEAFE', text: '#1E40AF' },
  EN_APROBACION: { bg: '#FEF3C7', text: '#92400E' },
  APROBADA: { bg: '#D1FAE5', text: '#065F46' },
  RECHAZADA: { bg: '#FEE2E2', text: '#991B1B' },
  CANCELADA: { bg: '#F3F4F6', text: '#9CA3AF' },
  DEVUELTA: { bg: '#FDE68A', text: '#78350F' },
};

function EstadoBadge({ estado }: { estado: string }) {
  const c = ESTADO_COLORS[estado] ?? ESTADO_COLORS.BORRADOR;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 10px',
        borderRadius: 12,
        background: c.bg,
        color: c.text,
      }}
    >
      {estado.replace(/_/g, ' ')}
    </span>
  );
}

export function MisSolicitudesPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'mis' | 'pendientes'>('mis');
  const [items, setItems] = useState<SolicitudResumen[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = tab === 'mis' ? '/requests/solicitudes/mis' : '/requests/solicitudes/pendientes';
    request<{ data: SolicitudResumen[] }>(url)
      .then((res) => setItems(res.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [tab]);

  return (
    <div
      style={{ padding: 32, maxWidth: 960, margin: '0 auto', fontFamily: 'DM Sans, sans-serif' }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <h1
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: '#1F3864',
            margin: 0,
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          {t.requests.misSolicitudes}
          <InfoTooltip
            title={t.structure.tooltips.misSolicitudes.titulo}
            text={t.structure.tooltips.misSolicitudes.ayuda}
          />
        </h1>
        <button
          onClick={() => navigate('/requests/nueva')}
          style={{
            border: 'none',
            borderRadius: 8,
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #1F3864, #2F5496)',
            color: 'white',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'inherit',
          }}
        >
          + {t.requests.nueva}
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #E5E7EB', marginBottom: 20 }}>
        {(['mis', 'pendientes'] as const).map((key) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'inherit',
              color: tab === key ? '#1F3864' : '#9CA3AF',
              borderBottom: tab === key ? '2px solid #1F3864' : '2px solid transparent',
              marginBottom: -2,
            }}
          >
            {key === 'mis' ? t.requests.misSolicitudes : t.requests.pendientesAprobar}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ color: '#9CA3AF', fontSize: 13, padding: 20 }}>{t.common.cargando}</div>
      ) : items.length === 0 ? (
        <div
          style={{
            color: '#9CA3AF',
            fontSize: 13,
            fontStyle: 'italic',
            padding: 40,
            textAlign: 'center',
          }}
        >
          {tab === 'mis' ? t.requests.sinSolicitudes : t.requests.sinPendientes}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((sol) => (
            <div
              key={sol.id}
              onClick={() => navigate(`/requests/${sol.id}`)}
              style={{
                padding: '14px 20px',
                borderRadius: 10,
                border: '1px solid #E5E7EB',
                background: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                transition: 'all 0.15s',
              }}
            >
              <span
                style={{
                  fontFamily: 'monospace',
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#2F5496',
                  minWidth: 80,
                }}
              >
                {sol.codigo ?? sol.id.slice(0, 8)}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>
                  {sol.tipoSolicitud?.nombre ?? '—'}
                </div>
                {sol.solicitante && (
                  <div style={{ fontSize: 12, color: '#6B7280' }}>
                    {sol.solicitante.nombre} {sol.solicitante.apellidos}
                  </div>
                )}
              </div>
              <EstadoBadge estado={sol.estado} />
              <span style={{ fontSize: 11, color: '#9CA3AF', minWidth: 80, textAlign: 'right' }}>
                {new Date(sol.creadoEn).toLocaleDateString('es-ES')}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
